/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    NotificationActions,
    NotificationContainer,
    STATES,
    TERRITORIES,
    ARMED_FORCES,
} from 'sarsaparilla';
import { useDocumentTitle } from 'shared-ui/utils/seo-utils';

import FindingTree from '../components/splashPage/FindingTree';
import TreeMap from '../components/splashPage/TreeMap';
import EveryKidOutdoorsPromo from '../components/splashPage/EveryKidOutdoorsPromo';
import HowItWorks from '../components/splashPage/HowItWorks';
// Not including the Ornament this year, so commenting out for now.
//import ChristmasOrnamentPromo from '../components/splashPage/ChristmasOrnamentPromo';
import HeroWithAutoComplete from '../components/splashPage/HeroWithAutoComplete';

import { fetchLocations } from '../actions/locations';
import { locationsSelector as locationsSel } from '../reducers';
import { onError } from '../utils';
import type { RootState, AppDispatch } from '../../dev/store';
import type { TreePermitData } from '../../types/permit';

// later entries win, so STATES takes priority if a code appears in more than one list
const STATE_LABELS: Record<string, string> = Object.fromEntries(
    [...ARMED_FORCES, ...TERRITORIES, ...STATES].map((item) => [item.value, item.label])
);

interface TreePermitProps {
    getTreePermits: () => Promise<Record<string, unknown>>;
    locations: Array<TreePermitData>;
    notify: (type: string, message: string) => void;
    setError: (error: { message?: string }, defaultErr: string) => string;
}

function TreePermit({ getTreePermits, locations, notify, setError }: TreePermitProps) {
    const facilitiesWithState = locations
        ?.filter((item) => Object.hasOwn(STATE_LABELS, item.state))
        .map((item) => ({ ...item, state: STATE_LABELS[item.state] }))
        .sort((a, b) => a.state.localeCompare(b.state) || a.name.localeCompare(b.name));

    const notifyError = (e: { message?: string }) => {
        const err = setError(e, 'There was a problem loading the tree permits.');
        notify('error', err);
    };

    useEffect(() => {
        getTreePermits().catch((e: { message?: string }) => notifyError(e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useDocumentTitle(`Christmas Tree Permits - ${process.env.SITE_NAME}`);

    return (
        <div className="tree-permit-splash-wrap" role="main">
            <HeroWithAutoComplete locations={facilitiesWithState} />
            <NotificationContainer className="mb-0 mx-6 mt-4" />
            <FindingTree />
            <HowItWorks />
            {/*<ChristmasOrnamentPromo />*/}
            <TreeMap locations={facilitiesWithState} />
            <EveryKidOutdoorsPromo />
        </div>
    );
}

const mapStateToProps = (state: RootState) => ({
    locations: locationsSel.getLocations(state) as TreePermitData[],
});

const mapDispatchToProps = (dispatch: AppDispatch) =>
    bindActionCreators(
        {
            getTreePermits: fetchLocations,
            notify: NotificationActions.setNotification,
            setError: onError,
        },
        dispatch
    );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connect(mapStateToProps, mapDispatchToProps)(TreePermit as any);
